import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { apiService } from '../../lib/api';
import { getTenantId } from '../../lib/config';
import { useNavigate } from 'react-router-dom';

interface RoomSlide {
  id: string;
  roomNumber: string;
  imageUrl: string;
}

/**
 * Landing Page Image Carousel
 * 
 * Modern carousel with Ken Burns effect, progress indicators, and smooth transitions
 */
export default function LandingImageCarousel() {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState<RoomSlide[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const intervalTime = 5000; // 5 seconds

  // Fetch rooms with images
  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const tenantId = getTenantId();
        
        // Fetch available rooms from public endpoint
        const response = await fetch(`${apiService['baseUrl']}/api/public/accommodations?tenantId=${tenantId}`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch accommodations: ${response.statusText}`);
        }

        const data = await response.json();
        
        if (data.success && data.data) {
          const allRooms = data.data.rooms || [];
          const companies = data.data.companies || [];
          
          // Get the first company ID from the companies array
          const firstCompanyId = companies.length > 0 ? companies[0].id : null;
          
          // Filter rooms by first company and only show active rooms (match accommodation section)
          const roomSlides: RoomSlide[] = allRooms
            .filter((room: any) => {
              return firstCompanyId && room.companyId === firstCompanyId && room.isActive;
            })
            .map((room: any) => {
              // Get the primary image or the first image, with fallback
              let imageUrl = '/images/webflow/IWACU-002.jpg'; // Default fallback
              
              if (room.images && room.images.length > 0) {
                const primaryImage = room.images.find((img: any) => img.isPrimary) || room.images[0];
                if (primaryImage && primaryImage.imageUrl) {
                  imageUrl = primaryImage.imageUrl.startsWith('http') 
                    ? primaryImage.imageUrl 
                    : `${apiService['baseUrl']}${primaryImage.imageUrl}`;
                }
              }
              
              return {
                id: room.id,
                roomNumber: room.roomNumber,
                imageUrl
              };
            });

          setRooms(roomSlides);
        }
        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch rooms:', error);
        setLoading(false);
      }
    };

    fetchRooms();
  }, []);

  // Progress bar animation
  useEffect(() => {
    if (rooms.length === 0 || isPaused) return;

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          return 0;
        }
        return prev + (100 / (intervalTime / 50));
      });
    }, 50);

    return () => clearInterval(progressInterval);
  }, [rooms.length, isPaused, intervalTime]);

  // Auto-rotate carousel
  useEffect(() => {
    if (rooms.length === 0 || isPaused) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % rooms.length);
      setProgress(0);
    }, intervalTime);

    return () => clearInterval(interval);
  }, [rooms.length, isPaused]);

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? rooms.length - 1 : prevIndex - 1
    );
    setProgress(0);
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => 
      (prevIndex + 1) % rooms.length
    );
    setProgress(0);
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    setProgress(0);
  };

  const handleRoomClick = (roomId: string) => {
    navigate(`/room/${roomId}`);
  };

  if (loading) {
    return (
      <div className="relative w-full h-[70vh] overflow-hidden bg-white flex items-center justify-center">
        <div className="flex flex-row items-center justify-center gap-5">
          <div className="w-20 h-20 bg-white rounded-lg flex items-center justify-center">
            <img 
              src="/brand-b.png" 
              alt="Ikoro House" 
              className="w-16 h-16 object-contain animate-pulse"
            />
          </div>
          <div className="text-3xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif', color: '#2C2416', letterSpacing: '2px' }}>
            IKOROHOUSE
            <span className="inline-block ml-1">
              <span className="animate-blink" style={{ color: '#d4a574', animationDelay: '0s' }}>.</span>
              <span className="animate-blink" style={{ color: '#d4a574', animationDelay: '0.2s' }}>.</span>
              <span className="animate-blink" style={{ color: '#d4a574', animationDelay: '0.4s' }}>.</span>
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (rooms.length === 0) {
    return (
      <div className="relative w-full h-[70vh] overflow-hidden bg-gray-200 flex items-center justify-center">
        <p className="text-gray-600">No rooms available</p>
      </div>
    );
  }

  return (
    <div 
      className="relative w-full h-[70vh] overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {rooms.map((room, index) => (
        <div
          key={room.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          {/* Ken Burns Effect - Slow Zoom */}
          <div className={`w-full h-full ${index === currentIndex ? 'animate-ken-burns' : ''}`}>
            <img
              src={room.imageUrl}
              alt={room.roomNumber}
              className="w-full h-full object-cover"
            />
          </div>
          
          {/* Gradient Overlays for Better Text Visibility */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/30"></div>
          
          {/* Room name overlay with modern animation */}
          <div className="absolute bottom-0 left-0 right-0 flex items-end justify-center pb-16 md:pb-20">
            <div 
              className={`text-center text-white px-4 transition-all duration-700 cursor-pointer ${
                index === currentIndex 
                  ? 'translate-y-0 opacity-100' 
                  : 'translate-y-10 opacity-0'
              }`}
              onClick={() => index === currentIndex && handleRoomClick(room.id)}
            >
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold drop-shadow-2xl mb-4 hover:text-gray-200 transition-colors">
                Discover Rooms {room.roomNumber}
              </h2>
              <div className="h-1 w-24 bg-white/80 mx-auto mb-4"></div>
              <p className="text-sm opacity-75">Click to view details</p>
            </div>
          </div>

          {/* Phone number overlay - Show on all slides */}
          <div 
            className={`absolute top-6 right-6 md:top-8 md:right-8 transition-all duration-700 ${
              index === currentIndex 
                ? 'translate-x-0 opacity-100' 
                : 'translate-x-10 opacity-0'
            }`}
          >
            <a 
              href="tel:+250791874212"
              className="block backdrop-blur-sm bg-white/10 border border-white/30 px-6 py-3 rounded-full shadow-xl hover:bg-white/20 transition-all cursor-pointer"
            >
              <p className="text-white font-semibold text-sm md:text-base flex items-center gap-2">
                <span className="text-xl">📞</span>
                <span>+ 250 791 874 212</span>
              </p>
            </a>
          </div>
        </div>
      ))}
      
      {/* Modern Navigation Arrows */}
      <button
        onClick={goToPrevious}
        className="absolute left-6 top-1/2 transform -translate-y-1/2 bg-white/20 backdrop-blur-md hover:bg-white/30 text-white p-3 rounded-full transition-all z-20 border border-white/30 hover:scale-110"
        aria-label="Previous image"
      >
        <ChevronLeft size={28} />
      </button>

      <button
        onClick={goToNext}
        className="absolute right-6 top-1/2 transform -translate-y-1/2 bg-white/20 backdrop-blur-md hover:bg-white/30 text-white p-3 rounded-full transition-all z-20 border border-white/30 hover:scale-110"
        aria-label="Next image"
      >
        <ChevronRight size={28} />
      </button>
      
      {/* Modern Progress Indicators with Active Progress Bar */}
      <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex gap-3 z-20">
        {rooms.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className="relative group"
            aria-label={`Go to slide ${index + 1}`}
          >
            {/* Background bar */}
            <div className={`h-1 rounded-full transition-all ${
              index === currentIndex
                ? 'w-16 bg-white/40'
                : 'w-8 bg-white/30 group-hover:bg-white/50'
            }`}>
              {/* Progress bar */}
              {index === currentIndex && (
                <div 
                  className="h-full bg-white rounded-full transition-all duration-100"
                  style={{ width: `${progress}%` }}
                />
              )}
            </div>
          </button>
        ))}
      </div>

      {/* Slide Counter */}
      <div className="absolute top-6 left-6 z-20 bg-black/30 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
        <span className="text-white text-sm font-medium">
          {currentIndex + 1} / {rooms.length}
        </span>
      </div>
    </div>
  );
}

